import React, { useState } from 'react';
import { View, Text, TextInput, Alert, StyleSheet } from 'react-native';
import theme from '../../theme';
import storage from '../../lib/storage';
import { getBackendUrl } from '../../lib/backendUrl';
import { fetchWithRetry } from '../../lib/fetchWithRetry';
import AppButton from '../../components/AppButton';
import { useI18n } from '../../i18n/useI18n';

async function buildDeviceHeaders() {
  const deviceIdKey = 'kakeibo_device_id';
  let deviceId = await storage.getItem(deviceIdKey);
  if (!deviceId) {
    deviceId = `inst-${Date.now()}-${Math.random().toString(36).slice(2,9)}`;
    try { await storage.setItem(deviceIdKey, deviceId); } catch (_) {}
  }
  const platform = (typeof navigator !== 'undefined' && (navigator.product || 'web')) as string;
  return {
    'X-Device-Id': deviceId,
    'X-App-Id': 'kakeibo',
    'X-Platform': platform,
  } as Record<string,string>;
}

type Props = {
  onSuccess: (payload: { token?: string; user?: any }) => void;
  defaultBackend?: string;
};

export default function SignupForm({ onSuccess, defaultBackend }: Props) {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [serverErrorKey, setServerErrorKey] = useState<string | null>(null);

  async function handleSignUp() {
    // validate
    setServerError(null);
    const emailValid = validateEmail(email);
    const passwordValid = validatePassword(password);
    setEmailError(emailValid ? null : t('invalidEmail'));
    setPasswordError(passwordValid ? null : t('passwordMin4'));
    if (!emailValid || !passwordValid) return;
    try {
      setLoading(true);
      const backend = getBackendUrl(defaultBackend);
      const extra = await buildDeviceHeaders();
      const base = backend;
      const res = await fetchWithRetry(`${base}/auth/signup`, {
        method: 'POST',
        credentials: 'include', // ensure browser receives cookie
        headers: { 'Content-Type': 'application/json', ...extra },
        body: JSON.stringify({ email, password })
      }, { retries: 2 });
      if (!res.ok) {
        let errMsg = t('signupFailed');
        try {
          const body = await res.json().catch(() => null);
          if (body) {
            const raw = String(body.error || body.message || JSON.stringify(body)).trim();
            const low = raw.toLowerCase();
            if (res.status === 409 || low.includes('duplicate') || low.includes('already') || low.includes('exists') || low.includes('user exists') || low.includes('email')) {
              errMsg = t('userExists');
              setServerErrorKey('userExists');
            } else {
              errMsg = raw || errMsg;
              setServerErrorKey('signupFailed');
            }
          } else {
            const txt = await res.text();
            errMsg = txt || errMsg;
            setServerErrorKey('signupFailed');
          }
        } catch (_) {
          const txt = await res.text().catch(() => '');
          errMsg = txt || errMsg;
          setServerErrorKey('signupFailed');
        }
        setServerError(errMsg);
        throw new Error(errMsg);
      }
      const payload = await res.json();
      onSuccess(payload);
    } catch (err: any) {
      if (!serverError) setServerError(err.message || String(err));
      const alertMsg = serverErrorKey ? t(serverErrorKey) : (serverError || err.message || t('signupFailed'));
      Alert.alert(t('error'), alertMsg);
    } finally { setLoading(false); }
  }

  function validateEmail(v: string) {
    return !!v && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  }

  function validatePassword(v: string) {
    return !!v && v.length >= 4;
  }

  return (
    <View style={[styles.card, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}> 
      <Text style={[styles.label, { color: theme.colors.textPrimary }]}>{t('email')}</Text>
      <TextInput value={email} onChangeText={setEmail} autoCapitalize="none" keyboardType="email-address" style={[styles.input, { color: theme.colors.textPrimary, backgroundColor: theme.colors.background, borderColor: theme.colors.border }]} />
      {emailError ? <Text style={styles.error}>{emailError}</Text> : null}
      <Text style={[styles.label, { color: theme.colors.textPrimary }]}>{t('password')}</Text>
      <TextInput value={password} onChangeText={setPassword} secureTextEntry style={[styles.input, { color: theme.colors.textPrimary, backgroundColor: theme.colors.background, borderColor: theme.colors.border }]} />
      {passwordError ? <Text style={styles.error}>{passwordError}</Text> : null}
      {serverError ? <Text style={styles.error}>{serverError}</Text> : null}
      <View style={{ height: 12 }} />
      <AppButton title={loading ? t('pleaseWait') : t('signUp')} onPress={handleSignUp} disabled={loading} tone="success" />
    </View>
  );
}

const styles = StyleSheet.create({
  card: { width: '100%', maxWidth: 420, padding: 16, borderRadius: 8, borderWidth: 1 },
  label: { marginTop: 8, marginBottom: 4, fontWeight: '600' },
  input: { height: 40, borderWidth: 1, paddingHorizontal: 8, borderRadius: 4 },
  error: { color: theme.colors.error, marginTop: 6 }
});
